import { LocalNotifications, LocalNotificationSchema, PendingResult } from '@capacitor/local-notifications';
import { ButtonProps } from '@mui/material/Button';
import { differenceInMinutes, subMinutes } from 'date-fns';
import { MobileNotificationView } from '../../backbone/shared/views/MobileNotificationView';
import { showConfirmNotification } from '../../react/features/ConfirmNotification';
import { CordovaPlugins } from '../../shared/CordovaPlugins';
import { isReleaseBuild } from './debugging';
import { isAndroid, isWeb, isWindows } from './is';

const taskDueSoonLocalNotificationId = 'task_due_soon_local_notification';

interface ActionSheetOptions {
    title: string;
    subtitle?: string;
    androidTheme?: string;
    androidEnableCancelButton?: boolean;
    destructiveButtonLast?: boolean;
    addDestructiveButtonWithLabel?: boolean;
    buttonLabels: Array<string>;
    position: Array<number>;
    addCancelButtonWithLabel: string;
}

interface Command {
    id: number;
}

export const showLoadingDialog = (): void => {
    $('body #loading_dialog_wrapper').show();
};

export const hideLoadingDialog = (): void => {
    $('body #loading_dialog_wrapper').hide();
};

export const sendAlert = (raw_title: string, raw_message: string, success = () => {}, _error: () => void): void => {
    if (isWeb()) {
        alert(raw_message);
        success();
        return;
    }

    let title = raw_title;
    let message = raw_message;

    if (isWindows()) {
        // @igs(bhayles): not really sure how we should reconcile message dialog on windows with alerts
        // this will produce something that looks more correct than the default (which would show the alert as the title
        // of the message box)
        // for now, swap title and message so presentation looks better

        title = raw_message;
        message = raw_title;
    }

    CordovaPlugins().notification.alert(message, success, title, _t('mobile_dialog_alert_ok'));
};

export const actionSheet = async (
    raw_options: ActionSheetOptions,
    success = (_index: number) => {},
    error = () => {}
): Promise<void> => {
    if (!isWindows()) {
        CordovaPlugins().actionsheet.show(raw_options, success, error);
        return;
    }

    const options = { ...raw_options, title: raw_options.title || '' };

    const dialog = new Windows.UI.Popups.MessageDialog(options.subtitle, options.title);
    if (options.addCancelButtonWithLabel) {
        options.buttonLabels.push(options.addCancelButtonWithLabel);
    }
    for (let i = 0; i < options.buttonLabels.length; ++i) {
        dialog.commands.append(new Windows.UI.Popups.UICommand(options.buttonLabels[i], null, i + 1));
    }

    const command: Command = await dialog.showAsync({
        x: options.position[0],
        y: options.position[1]
    });

    const buttonIndex = command ? command.id : 3;
    success(buttonIndex);
    return;
};

export const actionSheetIOS = async (
    rawOptions: ActionSheetOptions,
    success: () => void,
    error: () => void
): Promise<void> => {
    const options = {
        ...rawOptions,
        destructiveButtonLast: !rawOptions.addDestructiveButtonWithLabel,
        addCancelButtonWithLabel: _t('mobile_action_sheet_cancel')
    };

    await actionSheet(options, success, error);
};

export const actionSheetAndroid = async (
    rawOptions: ActionSheetOptions,
    success: () => void,
    error: () => void
): Promise<void> => {
    const options = {
        ...rawOptions,
        androidTheme: CordovaPlugins().actionsheet.ANDROID_THEMES.THEME_DEVICE_DEFAULT_LIGHT,
        androidEnableCancelButton: true
    };
    await actionSheet(options, success, error);
};

export const confirmNotification = (
    message: string,
    success: (status: boolean) => void,
    title: string,
    yes: string,
    no: string,
    confirmProps: ButtonProps = {}
): void => {
    if (isWeb()) {
        showConfirmNotification(
            message,
            success,
            title || _t('mobile_common_zenput'),
            yes || _t('mobile_common_button_yes'),
            no || _t('mobile_common_button_no'),
            confirmProps
        );
        return;
    }

    CordovaPlugins().notification.confirm(
        message,
        (answer) => {
            success(answer === 1);
        },
        title || _t('mobile_common_zenput'),
        [yes || _t('mobile_common_button_yes'), no || _t('mobile_common_button_no')]
    );
};

export const toastNotification = (message: string, html_class: string = '', icon: string = '', time = 3000): void => {
    // Close the toast in x amount of time
    clearTimeout($f.mobile.notifications.closeToastTimeout); // in case there's already a toast showing, ignore it; we'll start over
    if (time !== 0) {
        // allow for 'permanent' toasts if time is explicitly set to 0
        $f.mobile.notifications.closeToastTimeout = setTimeout(() => {
            getActiveToastView().hide_notification();
        }, time);
    }
    // When someone calls MobileNotificationView::hide_notification(), destroy our timeouts
    $f.application.vent.off('notification:hiding');
    $f.application.vent.on('notification:hiding', () => {
        $f.mobile.notifications.activeToastInfo = null;

        if ($f.mobile.notifications.closeToastTimeout) {
            clearTimeout($f.mobile.notifications.closeToastTimeout);
            $f.mobile.notifications.closeToastTimeout = null;
        }
    });

    getActiveToastView().show_notification({
        msg: message,
        html_class: html_class,
        icon: icon,
        show_method: $f.mobile.notifications.activeToastInfo ? 'replace' : null
    });

    // never restore from the global notif
    if (
        !$f.application.content.currentView.r_leftmenu ||
        $f.application.content.currentView.r_leftmenu.currentView.is_open()
    ) {
        return;
    }

    // Save notification details for a restore. See @restore_active_toastNotification().
    $f.mobile.notifications.activeToastInfo = {
        message: message,
        html_class: html_class,
        icon: icon
    };
};

export const getActiveToastView = (): typeof MobileNotificationView => {
    try {
        const layout = $f.application.content.currentView;

        // Show global notification if left menu is open or the user hasn't logged into the main app yet
        if (
            $f.mobile.notifications.globalNotificationView &&
            (!layout.r_main_content || layout.r_leftmenu.currentView.is_open())
        ) {
            return $f.mobile.notifications.globalNotificationView;
        }

        // ...if we're in a modal
        if ($('#content_overlay .toast_notification').length) {
            return $f.application.content_overlay.currentView.notification;
        }

        // by default, show the current active view's notification
        return layout.r_main_content.currentView.notification;
    } catch (e) {
        // In some cases, there could be no notification view (e.g., in one of the webapp previews). Instead
        // of making each of our callers do a null check, let's just create a fake view for them to use.
        return new MobileNotificationView({
            $el: $('<div>')
        });
    }
};

export const showErrorMessage = (message: string, noIcon: boolean = false, time: number = 3000): void => {
    toastNotification(message || _t('mobile_message_show_error_message'), 'error', noIcon ? '' : 'df-alert', time);
    $f.application.vent.trigger('mobile:set_num_uploads');
};

export const showSuccessMessage = (message: string): void => {
    toastNotification(message || _t('mobile_message_show_success_message'), 'success', 'df-success');
};

export const showOfflineMessage = (message: string, noIcon: boolean): void => {
    showErrorMessage(message || _t('mobile_message_show_offline_message'), noIcon);
    $f.application.vent.trigger('mobile:set_num_uploads');
};

/**
 * Show an error explaining that the user doesn't have OS permission to
 * access the specified feature.
 * @param  {string} identifier The name of the feature the user is trying to access (e.g., "contacts").
 */
export const showPermissionDeniedMessage = (identifier: string): void => {
    const baseErrorMessage = isWeb()
        ? 'mobile_message_show_permission_denied_message_web'
        : 'mobile_message_show_permission_denied_message';
    showErrorMessage(_t(baseErrorMessage, { permission_name: identifier }), true);
};

export const showBeginningUploadMessage = (): void => {
    toastNotification(_t('mobile_message_show_begin_uploading_message'), '', 'df-upload');
};

export const showUploadProgress = (force: boolean): void => {
    const $upload_bg = $('.menu ul li a.upload_background');
    const $toolbarnav = $('.main_content:not(.stale) .toolbarnav');
    const $progress_bar = $toolbarnav.find('.loading_bar .progress');

    let is_any_uploading = false;
    $f.mobile.uploads.for_each_pending_upload_structure((submissions) => {
        if (submissions.is_uploading) {
            is_any_uploading = true;
        }
    });

    if (!is_any_uploading && !force) {
        // Hide loading spinner on web only if we were uploading and aren't anymore
        if (isWeb() && $toolbarnav.find('.loading_bar').is(':visible')) {
            hideLoadingDialog();
        }

        // Hide the loading bar/spinner:
        $toolbarnav.css('border-bottom', '').find('.loading_bar').hide();
        $upload_bg.find('.count').show();
        $upload_bg.find('.pending_uploads_loading').hide();

        $progress_bar.css('width', '0%'); // reset for next time

        return;
    }

    // Show the progress bar/spinner (only if not already shown):
    if (!$toolbarnav.find('.loading_bar').is(':visible')) {
        $toolbarnav.css('border-bottom', 'none').find('.loading_bar').show();
        $upload_bg.find('.count').hide();
        $upload_bg.find('.pending_uploads_loading').show();
    }

    // On web, also show spinner to prevent leaving page
    if (isWeb()) {
        showLoadingDialog();
    }

    // Finally, animate the progress bar to the correct positioning
    const new_progress = $f.mobile.uploads.get_total_files_upload_progress() + '%';
    if ($progress_bar.css('width') == new_progress) {
        return;
    } // don't proceed if previous value is same as new
    $progress_bar.css('width', new_progress);
};

export const restoreActiveToast = (): void => {
    if (!$f.mobile.notifications.activeToastInfo) {
        return;
    }
    if ($f.application.content.currentView.r_leftmenu.currentView.is_open()) {
        // never restore to the global notif
        $f.mobile.notifications.activeToastInfo = null;
        return;
    }

    getActiveToastView().show_notification({
        msg: $f.mobile.notifications.activeToastInfo.message,
        html_class: $f.mobile.notifications.activeToastInfo.html_class,
        icon: $f.mobile.notifications.activeToastInfo.icon,
        show_method: 'restore'
    });
};

interface TaskAttrs {
    id: number;
    title: string;
    assignee?: {
        id: number;
    };
    is_active: boolean;
    is_closed: boolean;
    date_due: {
        $date: number;
    };
    current_state: string;
}

const getLocalNotificationTimedelta = (minutesUntilDue: number): [string, number] => {
    // Avoid spamming users by sending repeated notifications too close together.
    // Schedule them every 60, 30, 15 and 5 minutes before due time.
    let minutesToSubtract = 60;

    if (minutesUntilDue < 60 && minutesUntilDue > 30) {
        minutesToSubtract = 30;
    } else if (minutesUntilDue < 30 && minutesUntilDue > 15) {
        minutesToSubtract = 15;
    } else if (minutesUntilDue < 15 && minutesUntilDue > 5) {
        minutesToSubtract = 5;
    }

    const notificationTitle =
        minutesToSubtract === 60
            ? _t('notification_title_due_in_one_hour')
            : _t('notification_task_due_in_minutes', { minutes: minutesToSubtract });

    return [notificationTitle, minutesToSubtract];
};

export const filterTasksForNotifications = async (tasks: TaskAttrs[]): Promise<LocalNotificationSchema[]> => {
    const { notifications: pendingNotifications }: PendingResult = await LocalNotifications.getPending();

    const existingIds = new Set(pendingNotifications.map((item) => item.id));

    const filteredTasks: TaskAttrs[] = tasks.filter((item) => !existingIds.has(item.id));

    const futureNotifications: LocalNotificationSchema[] = [];
    const dateNow = new Date(Date.now());

    filteredTasks.forEach((task) => {
        const dateDueTimestamp = task.date_due?.$date;

        const taskDateDue = dateDueTimestamp ? new Date(dateDueTimestamp) : null;

        if (
            !taskDateDue || // there must be a due date to know when to notify
            !task.is_active || // we notify only about active tasks
            task.is_closed || // we notify only about incomplete tasks
            (!!task.assignee?.id && task.assignee?.id !== $f.active_user.get('id')) || // assigned to a different user
            task.current_state === 'processing' // avoid scheduling notifications for completed tasks that are still being processed
        ) {
            return;
        }

        const minutesUntilDue = differenceInMinutes(taskDateDue, dateNow);

        if (minutesUntilDue < 5) {
            // too late, don't schedule another notification for this task
            return;
        }

        const [notificationTitle, minutesToSubtract] = getLocalNotificationTimedelta(minutesUntilDue);

        const dateWhenToNotify: Date = subMinutes(taskDateDue, minutesToSubtract);

        const redirectUrlPWA: string = '/mobile/#tasks/id/' + task.id;
        const redirectUrlNative: string = 'zenput://tasks/id/' + task.id;
        const redirectUrl: string = isWeb() ? redirectUrlPWA : redirectUrlNative;

        futureNotifications.push({
            id: task.id,
            title: notificationTitle,
            body: _t('task_is_due_soon', { task_title: task.title }),
            schedule: { at: dateWhenToNotify },
            sound: 'task_reminder.wav',
            extra: { redirectUrl },
            channelId: isAndroid() ? taskDueSoonLocalNotificationId : undefined
        });
    });

    return futureNotifications;
};

export const scheduleTaskDueSoonLocalNotifications = async (tasks: TaskAttrs[]): Promise<void> => {
    const showConsoleLogs = !isReleaseBuild();

    // Adding a specific channel helps Android users to better control their notification preferences
    if (isAndroid()) {
        try {
            await LocalNotifications.createChannel({
                id: taskDueSoonLocalNotificationId,
                name: 'Task due soon notifications',
                description: 'Local notifications',
                sound: 'task_reminder.wav',
                // 4 is IMPORTANCE_HIGH, learn more at:
                // https://developer.android.com/develop/ui/views/notifications/channels#importance
                // Some android devices may have pop-ups disabled by default.
                // Specifying the importance makes it so that pop-ups are enabled upon granting the notifications permission.
                importance: 4,
                vibration: true
            });
        } catch (error) {
            $f.mobile.logging.log_error('Error creating notification channel:', error, showConsoleLogs ? true : false);
        }
    }

    const futureNotifications = await filterTasksForNotifications(tasks);

    if (futureNotifications.length > 0) {
        try {
            await LocalNotifications.schedule({
                notifications: futureNotifications
            });
        } catch (error) {
            $f.mobile.logging.log_error('Error scheduling local notification:', error, showConsoleLogs ? true : false);
        }
    }

    if (showConsoleLogs) {
        // eslint-disable-next-line no-console
        console.log('Scheduled new local notifications: ', futureNotifications);

        const { notifications: finalPendingNotifications }: PendingResult = await LocalNotifications.getPending();
        // eslint-disable-next-line no-console
        console.log('Pending local notifications: ', finalPendingNotifications);
    }
};
